import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaArrowLeft, FaCheck, FaPlug, FaWhatsapp } from "react-icons/fa";
import { CheckCircle2, Copy, Eye, EyeOff } from "lucide-react";

import {
  useConnectWhatsAppMutation,
  useGetWhatsAppStatusQuery,
} from "../toolkitServices/whatsappApi";

const BACKEND_URL = "https://sarafaflow-backend.onrender.com";

const Spinner = ({ size = 15, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const inputClass =
  "w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400";

const labelClass = "text-[11px] font-medium text-slate-600";

const fadeUp = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.22, ease: "easeOut" },
  },
};

const WhatsAppSetup = () => {
  const authUser = useSelector((s) => s.auth?.user);

  const { data: statusData, isFetching } = useGetWhatsAppStatusQuery();
  const [connectWhatsApp, { isLoading }] = useConnectWhatsAppMutation();

  const connected = !!statusData?.isConnected;

  const tenantId = useMemo(() => {
    return (
      authUser?.tenantId ||
      authUser?.TenantId ||
      authUser?.tenantID ||
      authUser?.ClientCode ||
      authUser?.clientCode ||
      authUser?._id ||
      authUser?.id ||
      ""
    );
  }, [authUser]);

  const webhookUrl = tenantId
    ? `${BACKEND_URL}/api/whatsapp/wati/webhook/${tenantId}`
    : `${BACKEND_URL}/api/whatsapp/wati/webhook/TENANT_ID`;

  const [showSecret, setShowSecret] = useState(false);
  const [showWebhookUrl, setShowWebhookUrl] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  const [form, setForm] = useState({
    provider: "WATI",
    businessName: "",
    whatsappNumber: "",
    watiApiKey: "",
    watiBaseUrl: "",
    wabaId: "",
    phoneNumberId: "",
    catalogId: "",
    accessToken: "",
  });

  useEffect(() => {
    const d = statusData?.data;

    if (d) {
      setForm({
        provider: d.provider || "WATI",
        businessName: d.businessName || "",
        whatsappNumber: d.whatsappNumber || "",
        watiApiKey: d.watiApiKey || "",
        watiBaseUrl: d.watiBaseUrl || "",
        wabaId: d.wabaId || "",
        phoneNumberId: d.phoneNumberId || "",
        catalogId: d.catalogId || "",
        accessToken: d.accessToken || "",
      });
    }
  }, [statusData]);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopiedWebhook(true);
      toast.success("Webhook URL copied");
      setTimeout(() => setCopiedWebhook(false), 1200);
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.businessName?.trim() || !form.whatsappNumber?.trim()) {
      toast.error("Business name and WhatsApp number required");
      return;
    }

    if (form.provider === "WATI") {
      if (!form.watiApiKey?.trim() || !form.watiBaseUrl?.trim()) {
        toast.error("WATI API Key and Base URL required");
        return;
      }
    }

    if (form.provider === "META") {
      if (!form.phoneNumberId?.trim() || !form.accessToken?.trim()) {
        toast.error("Phone Number ID and Access Token required");
        return;
      }
    }

    try {
      await connectWhatsApp(form).unwrap();
      toast.success(
        connected
          ? "WhatsApp setup updated successfully"
          : "WhatsApp connected successfully",
      );
    } catch (err) {
      toast.error(err?.data?.message || "Failed to save WhatsApp setup");
    }
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {isFetching && !statusData && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-xs text-[#6b59c6]">
            <Spinner size={26} />
            <span>Loading setup…</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex shrink-0 items-center justify-between rounded-t-xl bg-[#9481ce] px-4 py-2 text-white">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/whatsapp-commerce"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
            title="Back"
          >
            <FaArrowLeft className="text-xs" />
          </Link>

          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15">
            <FaWhatsapp className="text-lg" />
          </span>

          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold tracking-tight">
              WhatsApp Setup
            </h2>
            <p className="truncate text-[11px] text-violet-100">
              Connect WhatsApp provider credentials
            </p>
          </div>
        </div>

        <span
          className={`ml-2 shrink-0 rounded-full px-3 py-1 text-[10px] font-semibold ${
            connected
              ? "bg-emerald-50 text-emerald-700"
              : "bg-rose-50 text-rose-700"
          }`}
        >
          {connected ? "Connected" : "Not Connected"}
        </span>
      </div>

      {/* Body */}
      <form
        onSubmit={handleSubmit}
        className="min-h-0 flex-1 overflow-y-auto bg-slate-50/80 p-3 sm:p-4"
      >
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mx-auto w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          {/* Section Header */}
          <div className="flex items-center gap-2 border-b bg-white px-4 py-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-violet-50 text-[#6b59c6]">
              <FaPlug className="text-xs" />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-800">
                Business Account Details
              </h3>
              <p className="text-[11px] text-slate-500">
                Provider, number, API configuration and webhook
              </p>
            </div>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
            <div className="min-w-0">
              <label className={labelClass}>Provider</label>
              <select
                className={inputClass}
                value={form.provider}
                onChange={handleChange("provider")}
              >
                <option value="WATI">WATI</option>
                <option value="META">Meta Direct API</option>
              </select>
            </div>

            <div className="min-w-0">
              <label className={labelClass}>
                Business Name <span className="text-rose-500">*</span>
              </label>
              <input
                className={inputClass}
                value={form.businessName}
                onChange={handleChange("businessName")}
                placeholder="Loyal Jewellers"
              />
            </div>

            <div className="min-w-0">
              <label className={labelClass}>
                WhatsApp Number <span className="text-rose-500">*</span>
              </label>
              <input
                className={inputClass}
                value={form.whatsappNumber}
                onChange={handleChange("whatsappNumber")}
                placeholder="9198XXXXXXXX"
              />
            </div>

            {form.provider === "WATI" && (
              <>
                <div className="min-w-0">
                  <label className={labelClass}>
                    WATI Base URL <span className="text-rose-500">*</span>
                  </label>
                  <input
                    className={inputClass}
                    value={form.watiBaseUrl}
                    onChange={handleChange("watiBaseUrl")}
                    placeholder="https://live-server.wati.io"
                  />
                </div>

                <div className="min-w-0 md:col-span-2">
                  <label className={labelClass}>
                    WATI API Key <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showSecret ? "text" : "password"}
                      className={`${inputClass} pr-10`}
                      value={form.watiApiKey}
                      onChange={handleChange("watiApiKey")}
                      placeholder="Enter WATI API key"
                    />

                    <button
                      type="button"
                      onClick={() => setShowSecret((prev) => !prev)}
                      className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      title={showSecret ? "Hide key" : "Show key"}
                    >
                      {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {form.provider === "META" && (
              <>
                <div className="min-w-0">
                  <label className={labelClass}>WABA ID</label>
                  <input
                    className={inputClass}
                    value={form.wabaId}
                    onChange={handleChange("wabaId")}
                    placeholder="Meta WABA ID"
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>
                    Phone Number ID <span className="text-rose-500">*</span>
                  </label>
                  <input
                    className={inputClass}
                    value={form.phoneNumberId}
                    onChange={handleChange("phoneNumberId")}
                    placeholder="Phone Number ID"
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Catalog ID</label>
                  <input
                    className={inputClass}
                    value={form.catalogId}
                    onChange={handleChange("catalogId")}
                    placeholder="Catalog ID"
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>
                    Access Token <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showSecret ? "text" : "password"}
                      className={`${inputClass} pr-10`}
                      value={form.accessToken}
                      onChange={handleChange("accessToken")}
                      placeholder="Meta access token"
                    />

                    <button
                      type="button"
                      onClick={() => setShowSecret((prev) => !prev)}
                      className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      title={showSecret ? "Hide token" : "Show token"}
                    >
                      {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Webhook */}
            <div className="min-w-0 md:col-span-2">
              <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3">
                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <h4 className="text-[12px] font-semibold text-slate-800">
                      Webhook URL
                    </h4>
                    <p className="text-[11px] text-slate-500">
                      Copy this URL and paste it in WATI webhook settings.
                    </p>
                  </div>

                  {form.provider === "WATI" && (
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowWebhookUrl((prev) => !prev)}
                        className="inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-3 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {showWebhookUrl ? (
                          <EyeOff size={13} />
                        ) : (
                          <Eye size={13} />
                        )}
                        {showWebhookUrl ? "Hide" : "Show"}
                      </button>

                      <button
                        type="button"
                        onClick={copyWebhookUrl}
                        className="inline-flex h-8 items-center gap-1 rounded-full border border-violet-200 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] hover:bg-violet-50"
                      >
                        {copiedWebhook ? (
                          <>
                            <CheckCircle2 size={13} />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy size={13} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                <div className="min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <code className="block max-w-full break-all text-[11px] leading-5 text-slate-700">
                    {form.provider === "WATI"
                      ? showWebhookUrl
                        ? webhookUrl
                        : "••••••••••••••••••••••••••••••••••••••••••••••"
                      : "Meta webhook uses Phone Number ID to identify tenant."}
                  </code>
                </div>


                {form.provider === "WATI" && !tenantId && (
                  <p className="mt-2 text-[11px] font-medium text-rose-600">
                    Tenant ID not found in auth user. Please check auth user
                    data.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse gap-2 border-t bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-end">
            <Link
              to="/whatsapp-commerce"
              className="inline-flex w-full items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
            >
              Cancel
            </Link>

            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#6b59c6] px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#5b4ab3] disabled:opacity-60 sm:w-auto"
            >
              {isLoading ? <Spinner size={13} /> : <FaCheck size={12} />}
              {isLoading
                ? "Saving..."
                : connected
                  ? "Update Setup"
                  : "Connect WhatsApp"}
            </motion.button>
          </div>
        </motion.div>
      </form>
    </div>
  );
};

export default WhatsAppSetup;